<template>
  <div
    :class="[
      `data-table data-table--${theme}`,
      { 'data-table--sticky': Boolean(sticky), 'data-table--no-header': !header },
    ]"
    :style="`--data-table-layout: ${gridLayout}; --data-table-sticky-top: ${typeof sticky === 'string' ? sticky : '0'}`"
  >
    <div class="data-table__row" v-if="header">
      <div class="data-table__cell data-table__cell-select" v-if="isSelectable">
        <CheckboxComponent
          class="data-table__checkbox"
          v-model="selectAll"
          :disabled="!valueComp.length"
          :indeterminate="Boolean(internSelected.length && internSelected.length < valueComp.length)"
        />
      </div>
      <div
        :class="[
          'data-table__cell',
          `data-table__cell--text-align-${col.align || 'start'}`,
          {
            'data-table__cell-sort': isSortable && sortableBy.includes(col.id),
            'data-table__cell-sort--active': col.id in (sortBy || {}),
          },
        ]"
        :key="col.id"
        :data-cell-header-id="col.id"
        v-for="col in columns.filter(({ disabled }) => !disabled)"
        @click="sort(col.id)"
      >
        <slot :name="`${col.id}-label`" :item="col">
          <span v-html="col.label"></span>
          <HelperPopover v-if="col.helper" class="ml-1" :template="col.helper" />
        </slot>
        <FontAwesomeIcon
          class="data-table__cell-sort-icon"
          :icon="['fas', (sortBy || {})[col.id] < 0 ? 'caret-up' : 'caret-down']"
        />
      </div>
      <div v-if="hasRowActions" class="data-table__cell data-table__cell-actions"></div>
    </div>
    <div class="data-table__row" v-for="(item, index) in value" :key="index">
      <div class="data-table__cell data-table__cell-select" v-if="isSelectable">
        <CheckboxComponent
          class="data-table__checkbox"
          v-model="selectedComp"
          :state="item"
          @click.native="$ev => onCheckboxClick($ev, index, item)"
          @mousedown.prevent.native
          :disabled="isDisabled(item)"
          :compare="internalCompare"
        />
      </div>
      <component
        :is="rowWrapper(item, index).component"
        class="data-table__row-wrapper"
        v-bind="rowWrapper(item, index).props"
        v-on="{
          ...rowWrapper(item, index).listeners,
          ...(isExpandable ? { click: () => toggleRow(index) } : {}),
        }"
      >
        <component
          :is="cellWrapper(item, col).component"
          v-bind="cellWrapper(item, col).props"
          v-on="cellWrapper(item, col).listeners"
          :class="`data-table__cell data-table__cell--text-align-${col.align || 'start'}`"
          v-for="(col, colIndex) in columns.filter(({ disabled }) => !disabled)"
          :key="`col-${col.id}`"
          :data-cell-id="col.id"
        >
          <slot :name="col.id" :item="item" :column="col" :row-index="index" :col-index="colIndex" />
        </component>
      </component>
      <div v-if="hasRowActions" class="data-table__cell data-table__cell-actions">
        <ButtonGroup>
          <slot name="primary-row-actions" :item="item" :row-index="index" />
          <template #secondary="{ visible }">
            <slot name="secondary-row-actions" :item="item" :visible="visible" :row-index="index" />
          </template>
        </ButtonGroup>
      </div>

      <transition name="data-table-expand">
        <div v-if="isExpandable && expandedRows.has(index)" class="data-table__row-expanded">
          <div class="data-table__row-expanded-inner">
            <slot name="expanded-row" :item="item" :index="index" />
          </div>
        </div>
      </transition>
    </div>
    <transition name="footer">
      <div v-if="internSelected && internSelected.length" class="data-table__footer">
        <DropdownComponent v-if="internSelected.length">
          <ButtonComponent theme="primary" :label="`${selectedLabel}`" :icon="['fal', 'ellipsis-v']" />
          <template #dropdown>
            <slot name="select-actions" />
            <ButtonComponent :label="$t('resetSelection')" :icon="['fal', 'times-square']" @click="reset" />
          </template>
        </DropdownComponent>
        <slot name="footer" />
        <span>
          <FontAwesomeIcon :icon="['fal', 'info-circle']" style="margin-right: 0.2rem" />
          <i18n path="tableFooterTip" tag="span">
            <Keyboard value="shift" />
          </i18n>
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
import { CheckboxComponent } from 'vue-elder-checkbox'
import { DropdownComponent } from 'vue-elder-dropdown'
import Keyboard from '@/components/Keyboard'
import ButtonGroup from '@kvass/button-group'
import HelperPopover from '@/components/HelperPopover/Component'
import Bus from '@/bus'

const createUserSelection = () => ({
  allSelected: false,
  selectedIds: [],
  excludedIds: [],
  items: [],
})
export default {
  props: {
    value: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    theme: {
      type: String,
      default: 'default',
      validator(val) {
        return ['default', 'border'].includes(val)
      },
    },
    compare: {
      type: Function,
      default: (a, b) => a.id === b.id,
    },
    sticky: [Boolean, String],
    sortBy: Object,

    totalItems: {
      type: Number,
    },
    sortableBy: {
      type: Array,
      default: () => [],
    },
    sortTransform: {
      type: Function,
      default: (item, value) => ({ [item.key]: value }),
    },
    cellWrapper: {
      type: Function,
      default: () => ({ component: 'div' }),
    },
    rowWrapper: {
      type: Function,
      default: () => ({ component: 'div' }),
    },
    resetSelectedOnUpdate: {
      type: Boolean,
      default: false,
    },
    paginate: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Array,
    },
    header: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Function,
      default: () => false,
    },
  },

  watch: {
    selected: {
      handler(newVal) {
        // Keep internSelected in sync when parent changes selected
        if (this.resetSelectedOnUpdate) this.internSelected = Array.isArray(newVal) ? [...newVal] : []
      },
    },
  },

  data() {
    return {
      lastSelectedIndex: [-1],
      internSelected: [],
      userSelection: createUserSelection(),
      expandedRows: new Set(),
    }
  },

  computed: {
    valueComp() {
      return this.value.filter(v => !this.isDisabled(v))
    },
    isSelectable() {
      if (this.paginate) return true

      return Boolean(this.$path('propsData.selected', this.$options))
    },
    isSortable() {
      return Boolean(this.$options.propsData.sortBy) && this.sortableBy.length
    },
    hasRowActions() {
      return ['primary-row-actions', 'secondary-row-actions'].some(k => this.$scopedSlots[k])
    },
    isExpandable() {
      return Boolean(this.$scopedSlots['expanded-row'])
    },
    selectedLabel() {
      const base = this.$t('selected').toLowerCase()
      let count = this.internSelected.length
      if (this.userSelection.allSelected) {
        const allLabel = `${this.$t('all')} ${this.totalItems || count || ''} ${base}`
        if (!this.userSelection.excludedIds.length) return allLabel
        if (this.paginate && this.totalItems) {
          count = this.totalItems - this.userSelection.excludedIds.length
        }
      }

      return `${count} ${base}`
    },

    selectAll: {
      get() {
        if (this.paginate) {
          return this.userSelection.allSelected
        }

        return Boolean(this.valueComp.length && this.valueComp.every(s => this.internSelected.includes(s)))
      },
      set(val) {
        this.userSelection.allSelected = val
        if (val) this.userSelection.selectedIds = []
        else this.userSelection.excludedIds = []
        return (this.selectedComp = val ? [...this.valueComp] : [])
      },
    },

    selectedComp: {
      get() {
        if (!this.internSelected.length && this.$path('selected.length')) {
          this.internSelected = this.selected
        }
        return this.internSelected
      },
      set(val) {
        let value = this.getSelectedValue(val)
        this.internSelected = val
        return this.$emit('update:selected', value)
      },
    },
    gridLayout() {
      return [
        this.isSelectable ? '2.5rem' : false,
        ...this.columns.filter(({ disabled }) => !disabled).map(c => c.size || 'auto'),
        this.hasRowActions ? '0px' : false,
      ]
        .filter(Boolean)
        .join(' ')
    },
  },
  methods: {
    reset() {
      this.selectedComp = []
      this.userSelection = createUserSelection()
    },
    toggleRow(index) {
      if (this.expandedRows.has(index)) {
        this.expandedRows.delete(index)
      } else {
        this.expandedRows.add(index)
      }
      this.expandedRows = new Set(this.expandedRows)
    },
    getSelectedValue(val) {
      this.userSelection.items = val

      if (!this.userSelection.allSelected) {
        this.userSelection.selectedIds = [...new Set(val.map(i => i.id))]
      }

      if (this.paginate) {
        return this.userSelection
      }
      return this.userSelection.items
    },
    internalCompare(a, b) {
      if (!this.paginate) return this.compare(a, b)

      if (a.id === b.id) {
        if (this.userSelection.excludedIds.includes(a.id)) return false
        return this.compare(a, b)
      }

      //used if userSelection.allSelected is true -> then we need to mark all items visually selected
      if (!this.internSelected.find(i => i.id === b.id && this.userSelection.allSelected)) {
        if (this.userSelection.excludedIds.includes(b.id)) return false
        return this.userSelection.allSelected
      }

      return this.compare(a, b)
    },
    sort(id) {
      if (!this.isSortable || !this.sortableBy.includes(id)) return
      let existingSort = (this.sortBy && this.sortBy[id]) || -1
      this.$emit('update:sortBy', { [id]: existingSort * -1 })
    },
    updateExcludedIds(item, selected) {
      const isInAllSelectedMode = this.userSelection.allSelected

      if (!item || !item.id || selected === undefined || !isInAllSelectedMode) return

      const itemIsExluded = this.userSelection.excludedIds.includes(item.id)

      if (!itemIsExluded) {
        return this.userSelection.excludedIds.push(item.id)
      } else {
        const index = this.userSelection.excludedIds.indexOf(item.id)
        if (index !== -1) {
          this.userSelection.excludedIds.splice(index, 1)
        }
      }
    },
    onCheckboxClick(event, index, item) {
      if (event.target.checked === undefined) return
      let target = this.lastSelectedIndex.slice(-1).pop()
      let items = []

      if (event.shiftKey) {
        let reversed = target > index
        let start = target + (reversed ? -1 : 1)

        while (reversed ? start > index : start < index) {
          let target = this.value[start]
          if (!this.internSelected.includes(target) && !this.isDisabled(target)) items.push(target)
          reversed ? start-- : start++
        }
      }

      if (event.target.checked) this.lastSelectedIndex.push(index)
      else if (target === index) this.lastSelectedIndex.pop()

      this.selectedComp = [...this.internSelected, ...items]

      this.updateExcludedIds(item, event.target.checked)
    },
  },
  mounted() {
    Bus.$on('data-table:reset', () => this.reset())
  },

  components: {
    CheckboxComponent,
    DropdownComponent,
    Keyboard,
    ButtonGroup,
    HelperPopover,
  },
}
</script>

<style lang="scss">
.data-table {
  width: 100%;
  display: grid;
  position: relative;

  grid-template-columns: var(--data-table-layout);

  &--sticky {
    .data-table__row:first-child .data-table__cell {
      z-index: 2;
      position: sticky;
      top: var(--data-table-sticky-top);
      border-bottom: 1px solid var(--border-color);
    }
  }

  &:not(.data-table--no-header) {
    .data-table__row:first-child {
      background-color: white;
      font-size: 0.8em;
      text-align: left;
    }
  }

  &__cell {
    display: flex;
    align-items: center;
    padding: 0.75rem;

    &--text-align {
      &-start {
        justify-content: start;
      }
      &-center {
        justify-content: center;
      }
      &-end {
        justify-content: end;
      }
    }

    &-sort {
      cursor: pointer;
      vertical-align: middle;

      &-icon {
        opacity: 0;
        margin-left: 0.25rem;
      }

      &:not(.data-table__cell-sort--active):hover {
        .data-table__cell-sort-icon {
          opacity: 0.3;
        }
      }

      &--active {
        .data-table__cell-sort-icon {
          opacity: 1;
        }
      }
    }

    &-actions {
      padding: 0;
      position: relative;
    }
  }

  &__row {
    display: contents;

    &:hover,
    &:focus-within {
      .data-table__cell-actions .kvass-button-group {
        display: flex;
      }
    }

    &-wrapper {
      display: contents;
    }

    .data-table--default & {
      &:nth-child(odd) {
        > .data-table__cell,
        > .data-table__row-wrapper > .data-table__cell {
          background-color: css-lighten('border-color', 8%);
        }
      }
    }

    .data-table--border & {
      &:not(:last-child) .data-table__cell {
        border-bottom: 1px solid css-alpha('border-color', 0.5);
      }

      &:first-child {
        .data-table__cell {
          background-color: inherit;
        }
      }
    }

    &-expanded {
      grid-column: 1 / -1;

      &-inner {
        padding-block: 1rem;
      }
    }

    .kvass-button-group {
      display: none;
      align-items: center;
      position: absolute;
      right: 0;

      margin: 0.5rem;
      animation: fadeInFromLeft 200ms ease;

      .elder-button > * {
        padding: 0.3rem 0.75rem !important;

        background-color: white;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        border-radius: var(--border-radius);
        overflow: hidden;
      }
    }
  }

  &__checkbox {
    &:not(.elder-checkbox--checked):not(.elder-checkbox--indeterminate) .elder-checkbox__box {
      border-color: var(--border-color);
    }
  }

  &__footer {
    z-index: 2;
    position: sticky;
    bottom: 0;
    width: 100%;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    grid-column: 1 / -1;
    font-size: var(--font-size-small);

    background-color: white;
    animation: fadeInUp 200ms ease-out;

    border-top: 1px solid var(--border-color);

    .data-table--default & {
      background-color: whitesmoke;
    }

    .keyboard {
      margin-inline: 0.25rem;
    }
  }
}

.data-table--default {
  .data-table__row:not(:first-child) {
    > .data-table__cell,
    > .data-table__row-wrapper > .data-table__cell {
      border-top: 1.5px solid var(--border-color);
    }
  }

  .data-table__row:has(.data-table__row-expanded) {
    > .data-table__cell,
    > .data-table__row-wrapper > .data-table__cell,
    .data-table__row-expanded {
      background-color: css-alpha('border-color', 0.3);
    }
  }
}

.footer-enter-active,
.footer-leave-active {
  transition: 200ms ease-out;
}

.footer-enter-from,
.footer-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.data-table-expand-enter-active,
.data-table-expand-leave-active {
  overflow: hidden;
}

.data-table-expand-enter-active,
.data-table-expand-enter-active .data-table__row-expanded-inner {
  transition: transform 180ms cubic-bezier(0.075, 0.82, 0.165, 1);
}

.data-table-expand-leave-active,
.data-table-expand-leave-active .data-table__row-expanded-inner {
  transition: transform 180ms cubic-bezier(0.835, 0, 0.925, 0.18);
}

.data-table-expand-enter .data-table__row-expanded-inner,
.data-table-expand-leave-to .data-table__row-expanded-inner {
  transform: translateY(-100%);
}
</style>
